import { useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useQuery, useQueryClient } from 'react-query';
import { ProductService } from '../services/ProductService';
import {
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Favorite as FavoriteIcon,
  Share as ShareIcon,
  ShoppingCart as ShoppingCartIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { CartService } from '../services/CartService';
import { WishlistService } from '../services/WishlistService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

const categories = [
  { value: 'engine', label: 'Engine Parts', icon: '🔧' },
  { value: 'transmission', label: 'Transmission', icon: '⚙️' },
  { value: 'brakes', label: 'Brakes', icon: '🛑' },
  { value: 'suspension', label: 'Suspension', icon: '🔩' },
  { value: 'electrical', label: 'Electrical', icon: '⚡' },
  { value: 'body', label: 'Body Parts', icon: '🚗' }
];

export default function Search() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filters, setFilters] = useState({
    category: '',
    keyword: '',
    sort: 'newest'
  });
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { t } = useTranslation();

  const { data, isLoading, error } = useQuery(
    ['products', filters],
    () => ProductService.searchProducts(filters),
    {
      retry: 1,
      staleTime: 0,
      cacheTime: 0,
      onError: (error) => {
        console.error('Search error:', error);
      }
    }
  );

  const handleSearch = (event) => {
    event.preventDefault();
    // The search is now handled automatically by the query
  };

  const handleFilterChange = (type, value) => {
    setFilters(prev => ({
      ...prev,
      [type]: value,
      // Reset sort when searching
      ...(type === 'keyword' && { sort: 'newest' })
    }));
  };

  const handleAddToCart = async (product) => {
    try {
      if (!currentUser) {
        navigate('/auth');
        return;
      }

      // Check if product is out of stock
      if (product.stock <= 0) {
        enqueueSnackbar('This product is out of stock', { variant: 'error' });
        return;
      }

      // Get current cart to check existing quantity
      const currentCart = await CartService.getCart();
      const existingItem = currentCart.items.find(item => item.productId === product.id);
      const currentQuantity = existingItem?.quantity || 0;

      // Check if adding one more would exceed stock
      if (currentQuantity + 1 > product.stock) {
        enqueueSnackbar(`Only ${product.stock} units available`, { variant: 'warning' });
        return;
      }

      await CartService.addToCart(product.id);
      queryClient.invalidateQueries('cart');
      enqueueSnackbar(t('product.cart.addedToCart'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('product.cart.failedToAdd'), { variant: 'error' });
    }
  };

  const handleAddToWishlist = async (product) => {
    try {
      if (!currentUser) {
        navigate('/auth');
        return;
      }
      await WishlistService.addToWishlist(product.id);
      queryClient.invalidateQueries('wishlist');
      enqueueSnackbar(t('product.wishlist.addedToWishlist'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const handleShare = async (product) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: product.title,
          text: product.description,
          url: window.location.origin + '/product/' + product.id
        });
      } else {
        // Fallback for browsers that don't support Web Share API
        await navigator.clipboard.writeText(
          window.location.origin + '/product/' + product.id
        );
        enqueueSnackbar('Link copied to clipboard', { variant: 'success' });
      }
    } catch (error) {
      // Only show error if it's not a user cancellation
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
        enqueueSnackbar('Failed to share product', { variant: 'error' });
      }
    }
  };

  if (error?.message?.includes('index is being created')) {
    return (
      <Alert 
        severity="info" 
        sx={{ 
          m: 2, 
          borderRadius: 2,
          boxShadow: theme.shadows[2]
        }}
      >
        Search index is being created. This may take a few minutes. The page will automatically refresh when ready.
      </Alert>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: 'white',
          borderRadius: 2
        }}
      >
        <Typography variant="h3" gutterBottom fontWeight="bold">
          {t('search.mainTitle')}
        </Typography>
        <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
          {t('search.subTitle')}
        </Typography>

        {/* Search Form */}
        <Paper
          component="form"
          onSubmit={handleSearch}
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            maxWidth: 800
          }}
        >
          <TextField
            fullWidth
            placeholder={t('search.searchParts')}
            value={filters.keyword}
            onChange={(e) => handleFilterChange('keyword', e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{t('search.category')}</InputLabel>
            <Select
              value={filters.category}
              label={t('search.category')}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <MenuItem value="">{t('search.allCategories')}</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{category.icon}</span>
                    {t(`search.categories.${category.value.toLowerCase()}`)}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            sx={{
              height: 56,
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              px: 2,
              minWidth: 'auto',
              '& .MuiButton-startIcon': {
                marginRight: 0.5
              }
            }}
          >
            {t('search.searchParts')}
          </Button>
        </Paper>
      </Paper>

      {/* Filters and Sort */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button startIcon={<FilterListIcon />}>
          {t('search.filters')}
        </Button>
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel>{t('search.sortBy')}</InputLabel>
          <Select
            value={filters.sort}
            label={t('search.sortBy')}
            onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
          >
            <MenuItem value="newest">{t('search.sortOptions.newest')}</MenuItem>
            <MenuItem value="price_low">{t('search.sortOptions.priceLow')}</MenuItem>
            <MenuItem value="price_high">{t('search.sortOptions.priceHigh')}</MenuItem>
            <MenuItem value="popular">{t('search.sortOptions.popular')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Results */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('search.loadError')}
        </Alert>
      ) : data?.products?.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('search.noResults')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('search.tryDifferentFilters')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {data?.products?.map((product) => (
            <Grid item key={product.id} xs={12} sm={6} md={4} lg={3}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
                onClick={() => navigate(`/product/${product.id}`)}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={product.images?.[0] || '/placeholder-image.jpg'}
                  alt={product.title}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent>
                  <Typography gutterBottom variant="h6" component="h2" noWrap>
                    {product.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 2
                    }}
                  >
                    {product.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip
                      label={t(`product.categories.${product.category.toLowerCase()}`)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={t(`product.conditions.${product.condition.toLowerCase()}`)}
                      size="small"
                      color={product.condition === 'New' ? 'success' : 'default'}
                    />
                  </Box>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ${product.price.toFixed(2)}
                  </Typography>
                </CardContent>
                <CardActions 
                  sx={{ 
                    justifyContent: 'space-between', 
                    px: 2,
                    '& .MuiIconButton-root': {
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={t('product.addToWishlist')}>
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToWishlist(product);
                        }}
                      >
                        <FavoriteIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('search.shareProduct')}>
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShare(product);
                        }}
                      >
                        <ShareIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<ShoppingCartIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(product);
                    }}
                    size="small"
                    disabled={product.stock <= 0}
                    sx={{
                      ...(product.stock <= 0 && {
                        bgcolor: 'action.disabledBackground',
                        '&:hover': {
                          bgcolor: 'action.disabledBackground'
                        }
                      })
                    }}
                  >
                    {product.stock <= 0 ? 'Out of Stock' : t('product.addToCart')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 200,
            mt: 1
          }
        }}
      >
        <MenuItem 
          onClick={() => {
            navigate(`/product/${selectedProduct?.id}`);
            setMenuAnchor(null);
          }}
          sx={{ 
            py: 1,
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary={t('search.viewProduct')} />
        </MenuItem>

        <MenuItem 
          onClick={() => {
            handleShare(selectedProduct);
            setMenuAnchor(null);
          }}
          sx={{ 
            py: 1,
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          <ListItemIcon>
            <ShareIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText primary={t('search.shareProduct')} />
        </MenuItem>
      </Menu>
    </Container>
  );
} 